import React, { useState, useEffect, useRef } from "react";
import { saApi } from "../../../api/superAdminApi";
import { ROLE_OPTIONS } from "../../../constants/roles";
import {
  Stack, FormControl, FormLabel, Input, Select, Button, HStack, Box,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton,
  ModalBody, ModalFooter, useToast, Spacer, Text,
  AlertDialog, AlertDialogOverlay, AlertDialogContent, AlertDialogHeader,
  AlertDialogBody, AlertDialogFooter, useDisclosure,
} from "@chakra-ui/react";

const EditCompanyUserModal = ({
  isOpen,
  onClose,
  user,
  units,
  companyId,
  onSaved,
}) => {
  const toast = useToast();
  const [editingUser, setEditingUser] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // Demote flow state
  const [isDemoting, setIsDemoting] = useState(false);
  const [demoteBuSelections, setDemoteBuSelections] = useState({});
  const { isOpen: isDemoteConfirmOpen, onOpen: openDemoteConfirm, onClose: closeDemoteConfirm } = useDisclosure();
  const [isDemoteSubmitting, setIsDemoteSubmitting] = useState(false);
  const cancelDemoteRef = useRef();

  useEffect(() => {
    if (isOpen && user) {
      setEditingUser({ ...user });
      setIsDemoting(false);
      setDemoteBuSelections({});
    }
  }, [isOpen, user]);

  const handleClose = () => {
    setEditingUser(null);
    setIsUpdating(false);
    setIsDemoting(false);
    setDemoteBuSelections({});
    onClose();
  };

  const handleSave = async () => {
    if (!editingUser) return;
    try {
      setIsUpdating(true);
      const payload = {
        first_name: editingUser.first_name,
        last_name: editingUser.last_name,
        role: editingUser.role,
        job_title: editingUser.job_title,
        reporting_manager: editingUser.reporting_manager,
      };
      await saApi.updateCompanyUser(companyId, editingUser.id, payload);
      toast({ title: "User updated", status: "success" });
      handleClose();
      if (onSaved) onSaved();
    } catch (e) {
      const message = e?.response?.data?.message || e?.response?.data?.detail || e?.message || "Update failed";
      toast({ title: "Update failed", description: message, status: "error" });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRemoveFromCompany = async () => {
    if (!editingUser?.id) return;
    try {
      await saApi.removeUserFromCompany(companyId, editingUser.id);
      toast({ title: "User removed from company", status: "success" });
      handleClose();
      if (onSaved) onSaved();
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || "Remove failed";
      toast({ title: "Failed to remove user", description: message, status: "error" });
    }
  };

  const toggleDemoteBU = (buId) => {
    const id = String(buId);
    setDemoteBuSelections((prev) => {
      if (prev[id]) {
        const next = { ...prev };
        delete next[id];
        return next;
      }
      return { ...prev, [id]: "Sales Representative" };
    });
  };

  const selectedDemoteBUCount = Object.keys(demoteBuSelections).length;

  const handleDemoteConfirm = async () => {
    if (!editingUser?.id || selectedDemoteBUCount === 0) return;
    try {
      setIsDemoteSubmitting(true);
      await saApi.bulkUpdateUserBUs(companyId, editingUser.id, {
        add: Object.entries(demoteBuSelections).map(([buId, role]) => ({
          business_unit_id: parseInt(buId),
          role,
        })),
        remove: [],
        update: [],
      });
      toast({ title: "User demoted to BU user", status: "success" });
      closeDemoteConfirm();
      handleClose();
      if (onSaved) onSaved();
    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data?.detail || err?.message || "Demotion failed";
      toast({ title: "Demotion failed", description: message, status: "error" });
    } finally {
      setIsDemoteSubmitting(false);
    }
  };

  const sortedUnits = (Array.isArray(units) ? units : []).slice().sort((a, b) =>
    String(a.unit_name || a.unit_code || a.id).localeCompare(
      String(b.unit_name || b.unit_code || b.id)
    )
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit company user</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {editingUser && (
            <Stack spacing={4}>
              <FormControl isRequired>
                <FormLabel>First name</FormLabel>
                <Input value={editingUser.first_name || ""} onChange={(e) => setEditingUser({ ...editingUser, first_name: e.target.value })} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Last name</FormLabel>
                <Input value={editingUser.last_name || ""} onChange={(e) => setEditingUser({ ...editingUser, last_name: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>Role</FormLabel>
                <Input value="Company Admin" isDisabled />
              </FormControl>
              <FormControl>
                <FormLabel>Job title</FormLabel>
                <Input value={editingUser.job_title || ""} onChange={(e) => setEditingUser({ ...editingUser, job_title: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>Reporting manager</FormLabel>
                <Input value={editingUser.reporting_manager || ""} onChange={(e) => setEditingUser({ ...editingUser, reporting_manager: e.target.value })} />
              </FormControl>

              {/* Demote flow */}
              {!isDemoting ? (
                <HStack pt={2}>
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="orange"
                    onClick={() => setIsDemoting(true)}
                  >
                    Demote to BU user
                  </Button>
                  <Spacer />
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="red"
                    onClick={handleRemoveFromCompany}
                  >
                    Remove from Company
                  </Button>
                </HStack>
              ) : (
                <Box borderWidth="1px" borderRadius="md" p={3}>
                  <Text fontWeight="semibold" mb={2} fontSize="sm">
                    Assign business units for the demoted user:
                  </Text>
                  <Stack spacing={2}>
                    {sortedUnits.map((bu) => {
                      const id = String(bu.id);
                      const label = bu.unit_name || bu.unit_code || `${bu.id}`;
                      const isSelected = !!demoteBuSelections[id];
                      return (
                        <HStack key={id} spacing={3}>
                          <Button
                            size="xs"
                            variant={isSelected ? "solid" : "outline"}
                            colorScheme={isSelected ? "teal" : "gray"}
                            onClick={() => toggleDemoteBU(id)}
                            minW="120px"
                            justifyContent="flex-start"
                          >
                            {isSelected ? "✓ " : ""}{label}
                          </Button>
                          {isSelected && (
                            <Select
                              size="sm"
                              value={demoteBuSelections[id]}
                              onChange={(e) => setDemoteBuSelections((prev) => ({ ...prev, [id]: e.target.value }))}
                              maxW="200px"
                            >
                              {ROLE_OPTIONS.map((r) => (
                                <option key={r} value={r}>{r}</option>
                              ))}
                            </Select>
                          )}
                        </HStack>
                      );
                    })}
                  </Stack>
                  <HStack mt={3}>
                    <Button size="sm" variant="ghost" onClick={() => { setIsDemoting(false); setDemoteBuSelections({}); }}>
                      Cancel
                    </Button>
                    <Spacer />
                    <Button
                      size="sm"
                      colorScheme="orange"
                      isDisabled={selectedDemoteBUCount === 0}
                      onClick={openDemoteConfirm}
                    >
                      Confirm demotion
                    </Button>
                  </HStack>
                </Box>
              )}
            </Stack>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>Cancel</Button>
          <Button colorScheme="blue" onClick={handleSave} isLoading={isUpdating}>Save</Button>
        </ModalFooter>
      </ModalContent>

      <AlertDialog
        isOpen={isDemoteConfirmOpen}
        leastDestructiveRef={cancelDemoteRef}
        onClose={closeDemoteConfirm}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Demote to BU user
            </AlertDialogHeader>
            <AlertDialogBody>
              This will remove Company Admin access from {editingUser?.first_name} {editingUser?.last_name} and
              assign them to {selectedDemoteBUCount} business unit{selectedDemoteBUCount > 1 ? "s" : ""} with
              the selected roles.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelDemoteRef} onClick={closeDemoteConfirm}>
                Cancel
              </Button>
              <Button colorScheme="orange" onClick={handleDemoteConfirm} isLoading={isDemoteSubmitting} ml={3}>
                Demote
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Modal>
  );
};

export default EditCompanyUserModal;
